/**
 * HighlightingContext - Manages bi-directional highlighting between fields and PDF
 * Based on requirements 3.1, 3.2, 3.3, 3.4
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import {
  HighlightingContextState,
  HighlightingContextAction,
  HighlightingContextValue,
  BoundingBox,
  HighlightRegion,
  PDFPageViewport
} from '../types';
import { 
  transformBoundingBoxesToHighlights,
  validateBoundingBox 
} from '../utils/coordinateTransformer';
import { useCoordinateErrorHandler } from '../components/error';

// Initial state
const initialState: HighlightingContextState = {
  activeFieldId: null,
  highlightedRegions: [],
  currentPage: 1,
  pdfViewport: null,
  zoomLevel: 1.0,
  panOffset: { x: 0, y: 0 },
};

// Highlighting reducer
function highlightingReducer(
  state: HighlightingContextState,
  action: HighlightingContextAction
): HighlightingContextState {
  switch (action.type) {
    case 'SET_ACTIVE_FIELD': {
      return {
        ...state,
        activeFieldId: action.payload,
      };
    }

    case 'SET_ALL_HIGHLIGHTS': {
      return {
        ...state,
        // Only set activeFieldId to 'all-fields' if no active field is currently set
        activeFieldId: state.activeFieldId || 'all-fields',
        highlightedRegions: action.payload,
      };
    }

    case 'CLEAR_HIGHLIGHTS':
      return {
        ...state,
        activeFieldId: null,
        highlightedRegions: [],
      };

    case 'UPDATE_VIEWPORT': {
      const { viewport, page } = action.payload;
      
      return {
        ...state,
        pdfViewport: viewport,
        currentPage: page,
      };
    }

    case 'SET_ZOOM_LEVEL': {
      const newZoomLevel = action.payload;
      
      return {
        ...state,
        zoomLevel: newZoomLevel,
        // Clear highlights when zoom changes - they will be re-created when field is focused
        highlightedRegions: [],
      };
    }

    case 'SET_PAN_OFFSET': {
      const newPanOffset = action.payload;
      
      return {
        ...state,
        panOffset: newPanOffset,
        // Clear highlights when pan changes - they will be re-created when field is focused
        highlightedRegions: [],
      };
    }

    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: action.payload,
        // Clear highlights when changing pages - they'll be refreshed by the page change effect
        highlightedRegions: [],
      };

    default:
      return state;
  }
}

/**
 * Filter bounding boxes to only include those on the current page
 */
function filterBoundingBoxesByPage(
  boundingBoxes: BoundingBox[],
  currentPage: number
): BoundingBox[] {
  return boundingBoxes.filter(bbox => bbox.page === currentPage);
}

// Context creation
const HighlightingContext = createContext<HighlightingContextValue | undefined>(undefined);

// Provider component
interface HighlightingProviderProps {
  children: React.ReactNode;
}

export function HighlightingProvider({ children }: HighlightingProviderProps) {
  const [state, dispatch] = useReducer(highlightingReducer, initialState);
  const coordinateErrorHandler = useCoordinateErrorHandler();



  // Action creators
  const showAllFields = useCallback((allFields: Array<{id: string, boundingBoxes: BoundingBox[]}>) => {
    try {
      const allHighlights: HighlightRegion[] = [];
      
      allFields.forEach(field => {
        if (field.boundingBoxes && field.boundingBoxes.length > 0) {
          const validBoundingBoxes = field.boundingBoxes.filter(bbox => {
            const isValid = validateBoundingBox(bbox);
            if (!isValid) {
              console.warn(`Invalid bounding box for field ${field.id}:`, bbox);
            }
            return isValid;
          });
          
          // Filter to current page only
          const currentPageBoxes = filterBoundingBoxesByPage(validBoundingBoxes, state.currentPage);
          
          if (currentPageBoxes.length > 0 && state.pdfViewport) {
            const highlights = transformBoundingBoxesToHighlights(
              field.id,
              currentPageBoxes,
              state.pdfViewport
            );
            allHighlights.push(...highlights);
          }
        }
      });
      
      dispatch({
        type: 'SET_ALL_HIGHLIGHTS',
        payload: allHighlights
      });
      
    } catch (error) {
      console.error('Error showing all fields:', error);
    }
  }, [state.currentPage, state.pdfViewport]);

  const setActiveField = useCallback((fieldId: string, boundingBoxes?: BoundingBox[]) => {
    console.log(`🎯 setActiveField called: ${fieldId}, page: ${state.currentPage}, boundingBoxes:`, boundingBoxes?.length || 0);
    
    // Set the active field
    dispatch({
      type: 'SET_ACTIVE_FIELD',
      payload: fieldId
    });
    
    // The highlights should already be visible from showAllFields
    // The HighlightOverlay will automatically show the active field in blue
    // and all others in yellow based on the activeFieldId
    
  }, [state.currentPage]);





  const clearHighlights = useCallback(() => {
    dispatch({ type: 'CLEAR_HIGHLIGHTS' });
  }, []);

  const updateViewport = useCallback((viewport: PDFPageViewport, page: number) => {
    dispatch({ type: 'UPDATE_VIEWPORT', payload: { viewport, page } });
  }, []);



  const setZoomLevel = useCallback((zoom: number) => {
    // Clamp zoom level to reasonable bounds
    const clampedZoom = Math.max(0.1, Math.min(5.0, zoom));
    dispatch({ type: 'SET_ZOOM_LEVEL', payload: clampedZoom });
  }, []);

  const setPanOffset = useCallback((offset: { x: number; y: number }) => {
    dispatch({ type: 'SET_PAN_OFFSET', payload: offset });
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    if (page > 0) {
      console.log(`📄 Changing page from ${state.currentPage} to ${page}`);
      dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
    }
  }, [state.currentPage]);

  const contextValue: HighlightingContextValue & { coordinateErrorHandler: typeof coordinateErrorHandler } = {
    state,
    actions: {
      setActiveField,
      showAllFields,
      clearHighlights,
      updateViewport,
      setZoomLevel,
      setPanOffset,
      setCurrentPage,
    },
    coordinateErrorHandler,
  };

  return (
    <HighlightingContext.Provider value={contextValue}>
      {children}
    </HighlightingContext.Provider>
  );
}

// Hook for using the context
export function useHighlighting() {
  const context = useContext(HighlightingContext);
  if (context === undefined) {
    throw new Error('useHighlighting must be used within a HighlightingProvider');
  }
  return context;
}

// Alias for consistency with component imports
export const useHighlightingContext = useHighlighting;

// Export utility functions for use in other components
export {
  validateBoundingBox,
  filterBoundingBoxesByPage,
};